import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Wine, Users, Sparkles, Calendar, Award, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { fmtDateShortParis } from "../lib/date";
import { useSiteContent } from "../contexts/SiteContentContext";
import FleurKintsugi from "../components/brand/FleurKintsugi";
import KintsugiScrollVein from "../components/brand/KintsugiScrollVein";
import StoryTeaser from "../components/story/StoryTeaser";

const HERO_IMG_DEFAULT = "https://customer-assets-39nsmqrw.emergentagent.net/job_cheers-book/artifacts/g6o3mf4c_Saveurs%20Normandes%2C%20l%E2%80%99art%20du%20cocktail.png";
const RUM_IMG_DEFAULT = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/o6x6dk81_IMG_3252.jpeg";
const WEDDING_IMG_DEFAULT = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/cjvt5s97_IMG_2934.jpeg";
const TAPAS_IMG_DEFAULT = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/b1tp14xs_985ded16-b902-4898-8443-903836db9c74.jpeg";
const STOREFRONT_IMG_DEFAULT = "https://customer-assets.emergentagent.com/job_cheers-book/artifacts/tr5jv7du_IMG_3084.jpeg";

const eventTypeLabel = {
  afterwork: "Afterwork",
  atelier_cocktail: "Atelier Cocktail",
  atelier_rhum: "Atelier Rhum Arrangé",
};

export default function Home() {
  const [events, setEvents] = useState([]);
  const { c } = useSiteContent();
  const HERO_IMG = c("home_hero_image", HERO_IMG_DEFAULT);
  const RUM_IMG = c("home_rum_image", RUM_IMG_DEFAULT);
  const WEDDING_IMG = c("home_wedding_image", WEDDING_IMG_DEFAULT);
  const TAPAS_IMG = c("home_tapas_image", TAPAS_IMG_DEFAULT);
  const STOREFRONT_IMG = c("home_storefront_image", STOREFRONT_IMG_DEFAULT);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ═══════════════════════════════════════════════════════════════
          HERO — Temple de la Lave (split-frame asymétrique 55/45)
          ═══════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden bg-background"
        data-testid="hero-section"
      >
        {/* SVG gradient defs — reused by kintsugi veins */}
        <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
          <defs>
            <linearGradient id="kintsugi-gold-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#B57420" />
              <stop offset="30%" stopColor="#F5C370" />
              <stop offset="55%" stopColor="#FFE29A" />
              <stop offset="75%" stopColor="#D99533" />
              <stop offset="100%" stopColor="#7A4A14" />
            </linearGradient>
          </defs>
        </svg>

        {/* Ambient magma bg — 3 slow-breathing blobs behind everything */}
        <div className="magma-ambient" aria-hidden="true"><span className="magma-blob" /></div>

        {/* Custom gold cursor drop (handled globally by MagmaCursor at App root) */}

        {/* Vertical brand mark bookbinding (desktop) */}
        <div className="hidden lg:block absolute left-4 top-1/2 -translate-y-1/2 vertical-mark z-10" aria-hidden="true">
          Saveurs Normandes · Est. 2024 · Louviers
        </div>

        {/* Kintsugi crossing veins — SVG path that "bleeds" from photo → text */}
        <svg
          className="kintsugi-vein-svg hidden md:block"
          viewBox="0 0 1440 800"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {/* Vein 1 — main crack, from top-left across through hero */}
          <path d="M 40 60 Q 300 40, 520 180 T 900 320 Q 1080 400, 1280 380 T 1440 500" />
          {/* Vein 2 — secondary branch */}
          <path d="M 200 700 Q 380 620, 560 640 T 860 560 Q 1000 520, 1180 560" />
          {/* Vein 3 — tertiary fine crack */}
          <path d="M 60 400 Q 240 460, 440 420 T 720 460" />
        </svg>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-14 pt-24 pb-24 lg:pt-32 lg:pb-36 z-10">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">

            {/* ── Colonne gauche — texte sur panneau basalte ────────────── */}
            <div className="relative">
              <div
                className="text-[11px] uppercase tracking-[0.4em] text-primary/90 mb-8 reveal-hero font-semibold"
                style={{ animationDelay: "0ms" }}
              >
                {c("home_hero_eyebrow", "· Bar · Bières artisanales · Cocktails · Arrangés ·")}
              </div>

              <h1
                className="font-display leading-[0.98] tracking-[-0.02em] text-[3.2rem] sm:text-6xl lg:text-[5.5rem]"
                style={{ color: "#F4EDE0" }}
              >
                <span
                  className="block reveal-hero"
                  style={{ animationDelay: "200ms" }}
                >
                  {c("home_hero_title_line1", "Le bar à cocktails")}
                </span>
                <span
                  className="block italic molten-flow molten-flow-hoverable kintsugi-underline reveal-hero mt-2"
                  style={{ animationDelay: "500ms" }}
                >
                  {c("home_hero_title_line2", "au cœur de la Normandie.")}
                </span>
              </h1>

              {/* Fleur ornament + cursive annotation (rotated -3°) */}
              <div className="mt-10 flex items-center gap-5 reveal-rotate" style={{ animationDelay: "900ms" }}>
                <FleurKintsugi size={44} />
                <p
                  className="font-vibes text-3xl md:text-[2.4rem] leading-[1.1]"
                  style={{
                    color: "#F5C370",
                    textShadow: "0 0 24px rgba(245,195,112,0.35)",
                  }}
                  data-testid="hero-tagline"
                >
                  Plus qu'un spiritueux,<br />une histoire de Normandie.
                </p>
              </div>

              <p
                className="mt-8 text-base lg:text-lg text-foreground/85 max-w-xl leading-relaxed reveal-hero"
                style={{ animationDelay: "700ms" }}
              >
                {c("home_hero_subtitle", "Bar à cocktails à Louviers · bières artisanales locales, cocktails signatures, rhums et calvados arrangés primés au Frankfurt Trophy. Ateliers de mixologie et bar mobile pour mariages, EVG et réceptions partout en Normandie.")}
              </p>

              <div className="mt-10 flex flex-wrap gap-4 reveal-hero" style={{ animationDelay: "800ms" }}>
                <Button asChild size="lg" className="btn-gold-marble rounded-full px-8 h-12" data-testid="hero-events-btn">
                  <Link to="/evenements">Voir les événements <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full px-8 h-12 border-primary/50 hover:border-primary hover:bg-primary/10 text-foreground" data-testid="hero-private-btn">
                  <Link to="/evenement-prive">Privatiser le bar mobile</Link>
                </Button>
              </div>
            </div>

            {/* ── Colonne droite — photo cadre + cachet de cire ─────────── */}
            <div className="relative reveal-hero" style={{ animationDelay: "300ms" }}>
              {/* Photo frame (kintsugi hairline + broken corner) */}
              <div className="relative">
                <div className="kintsugi-frame aspect-[16/10] w-full">
                  <img
                    src={HERO_IMG}
                    alt="Bar Saveurs Normandes"
                    className="w-full h-full object-cover"
                    style={{ transform: "scale(1.02)" }}
                  />
                </div>

                {/* Wax seal — awards medallion pinned OUTSIDE the clipped frame */}
                <a
                  href="#awards"
                  onClick={(e) => {
                    e.preventDefault();
                    document.querySelector('[data-testid="awards-section"]')?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="wax-seal absolute -bottom-8 -left-6 lg:-left-12 hover:scale-[1.06] transition-transform duration-500 z-30"
                  aria-label="Deux médailles Frankfurt International Trophy 2026"
                  data-testid="hero-awards-strip"
                  style={{ textDecoration: "none" }}
                >
                  <div className="flex flex-col items-center text-center px-3">
                    <Award className="w-6 h-6 mb-0.5 text-[#3a1f05]" strokeWidth={2.4} />
                    <span className="text-[9px] uppercase tracking-[0.22em] font-black text-[#3a1f05] leading-tight">
                      Primés<br />2026
                    </span>
                    <span className="mt-1 text-[7px] uppercase tracking-[0.2em] text-[#3a1f05]/85 leading-tight">
                      Frankfurt<br />Trophy
                    </span>
                  </div>
                </a>
              </div>

              {/* Awards annotation — under the photo */}
              <div className="mt-10 pl-2 lg:pl-24 reveal-hero" style={{ animationDelay: "1100ms" }}>
                <div className="text-[10px] uppercase tracking-[0.32em] text-primary/80 mb-2">Distinctions Frankfurt Trophy</div>
                <div className="text-sm text-foreground/90">
                  <span className="font-display text-primary text-base">Grand Or</span> · Bois Sucré Intense
                </div>
                <div className="text-sm text-foreground/90">
                  <span className="font-display text-zinc-200 text-base">Argent</span> · Élixir des Anges
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Fleur ornament divider — brand signature between hero & content ─── */}
      <div className="relative py-8 lg:py-12 flex items-center justify-center gap-6" aria-hidden="true">
        <span className="h-px w-24 lg:w-40 bg-gradient-to-r from-transparent via-primary/60 to-primary/60" style={{ filter: "drop-shadow(0 0 6px rgba(217,149,51,0.55))" }} />
        <FleurKintsugi size={36} />
        <span className="h-px w-24 lg:w-40 bg-gradient-to-l from-transparent via-primary/60 to-primary/60" style={{ filter: "drop-shadow(0 0 6px rgba(217,149,51,0.55))" }} />
      </div>

      {/* SERVICES BENTO */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 overflow-hidden" data-testid="services-section">
        <KintsugiScrollVein variant="s-curve" strokes={2} opacity={0.5} />
        <div className="relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Nos services</div>
            <h2 className="font-display text-4xl lg:text-5xl">Trois manières de vivre l'expérience.</h2>
          </div>
          <p className="max-w-md text-muted-foreground">Une cave de spiritueux d'exception, une équipe créative, et une obsession : faire de chaque service un moment mémorable.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <ServiceCard
            to="/evenements"
            className="md:col-span-3 row-span-2"
            tall
            image={RUM_IMG}
            tag="Ateliers"
            title="Ateliers Rhum Arrangé & Mixologie"
            text="Initiez-vous au rhum arrangé et à la mixologie : épices, fruits frais, infusions. Repartez avec votre création."
            testid="service-rhum"
          />
          <ServiceCard
            to="/reservation-table"
            className="md:col-span-3"
            image={TAPAS_IMG}
            tag="Bar à cocktails"
            title="Au comptoir, à Louviers"
            text="Cocktails signatures, bières artisanales, arrangés maison et planches tapas. Mardi—Samedi 17h—23h."
            testid="service-table"
          />
          <ServiceCard
            to="/evenement-prive"
            className="md:col-span-3"
            image={WEDDING_IMG}
            tag="Bar Mobile"
            title="Mariages & Vins d'Honneur"
            text="Bar mobile premium pour vos mariages, vins d'honneur, EVG et réceptions privées."
            testid="service-wedding"
          />
        </div>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 overflow-hidden" data-testid="upcoming-events-section">
        <KintsugiScrollVein variant="horizontal" strokes={2} opacity={0.45} />
        <div className="relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">À venir</div>
            <h2 className="font-display text-4xl lg:text-5xl">Prochaines expériences</h2>
          </div>
          <Link to="/evenements" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-2" data-testid="see-all-events-link">
            Tous les événements <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {events.length === 0 ? (
            <div className="text-muted-foreground col-span-3">Aucun événement programmé pour le moment.</div>
          ) : (
            events.map((e) => (
              <Link
                key={e.id}
                to={`/evenements/${e.id}`}
                className="group rounded-xl overflow-hidden bg-card border border-border hover-lift"
                data-testid={`event-card-${e.id}`}
              >
                {e.image && (
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={e.image} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-primary mb-2">
                    {eventTypeLabel[e.type] || e.type}
                  </div>
                  <h3 className="font-display text-2xl mb-3">{e.title}</h3>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground inline-flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {fmtDateShortParis(e.date)}
                    </span>
                    <span className="gold-text font-medium">{e.price.toFixed(0)} €</span>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>
        </div>
      </section>

      {/* LE BAR — offering on premise (SEO: bar Louviers, bières artisanales, arrangés) */}
      <section className="relative overflow-hidden py-20 lg:py-28" data-testid="bar-section">
        <KintsugiScrollVein variant="corner-tl" strokes={2} opacity={0.5} />
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?w=1600&q=80"
            alt="Comptoir bar Saveurs Normandes"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-xs uppercase tracking-[0.35em] text-primary mb-5">· Le bar · Louviers ·</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Bières artisanales, cocktails signatures
              <br />
              <span className="italic gold-text">& arrangés maison.</span>
            </h2>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Une cave de spiritueux d'exception, une carte renouvelée chaque saison et l'envie de partager. Poussez la porte, le mardi au samedi de 17h à 23h.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
            <BarCard
              tag="Bières"
              title="Artisanales locales"
              text="Une sélection de brasseries indépendantes de Normandie : blondes, IPA, ambrées, blanches, brunes, sans alcool — au verre comme à la pression."
              image="https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=900&q=80"
              testid="bar-card-beers"
            />
            <BarCard
              tag="Cocktails"
              title="Signatures maison"
              text="Créations originales du chef barman : classiques twistés, créations saisonnières et mocktails élégants pour ceux qui ne boivent pas."
              image="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/b1tp14xs_985ded16-b902-4898-8443-903836db9c74.jpeg"
              testid="bar-card-cocktails"
            />
            <BarCard
              tag="Arrangés"
              title="Calvados & Rhum"
              text="Macérations longues d'épices, fruits et sucres nobles — incluant nos cuvées primées Grand Or et Argent au Frankfurt International Trophy 2026."
              image="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/j9k1igvw_IMG_2751.jpeg"
              testid="bar-card-arranges"
            />
            <BarCard
              tag="Spiritueux"
              title="Cave d'exception"
              text="Nos rhums arrangés maison en bouteille, y compris les cuvées primées 2026 — disponibles à la vente au bar ou en livraison."
              image="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/9k7zo5gf_4306700533884434252.jpeg"
              testid="bar-card-spirits"
              to="/boutique"
              ctaLabel="Visiter la boutique"
            />
          </div>

          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary/30 bg-card/70 backdrop-blur p-6 lg:p-8">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-1">Au comptoir</div>
              <div className="font-display text-2xl">Mardi — Samedi · 17h — 23h</div>
              <div className="text-sm text-muted-foreground mt-1">4 rue de la Laiterie, 27400 Louviers · 06 65 02 87 00</div>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/carte" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-11 text-sm font-medium" data-testid="bar-section-menu-btn">
                Voir la carte <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/reservation-table" className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-6 h-11 text-sm" data-testid="bar-section-book-btn">
                Réserver une table
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUITS NORMANDS */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 overflow-hidden" data-testid="normandy-section">
        <KintsugiScrollVein variant="corner-br" strokes={2} opacity={0.5} />
        <div className="relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Le terroir</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-6">Des saveurs normandes dans chaque verre.</h2>
            <p className="text-muted-foreground leading-relaxed mb-8 max-w-xl">
              Nos cocktails mettent à l'honneur les trésors de la Normandie : calvados de grande maison, pommeau, cidre fermier, crème d'Isigny, caramel au beurre salé et miel de verger. Chaque création est une invitation au voyage dans le bocage.
            </p>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                "Calvados Pays d'Auge",
                "Pommeau de Normandie",
                "Cidre fermier brut",
                "Poiré Domfrontais",
                "Crème d'Isigny AOP",
                "Caramel au beurre salé",
                "Pommes de Normandie",
                "Miel des vergers",
              ].map((p) => (
                <li key={p} className="flex items-center gap-2 text-muted-foreground" data-testid={`normandy-product-${p}`}>
                  <span className="w-1 h-1 rounded-full bg-primary" /> {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.pexels.com/photos/1407846/pexels-photo-1407846.jpeg?w=600" alt="Verger de Normandie" className="w-full h-64 object-cover rounded-xl border border-border" />
            <img src="https://images.pexels.com/photos/14009280/pexels-photo-14009280.jpeg?w=600" alt="Tapas normandes" className="w-full h-64 object-cover rounded-xl border border-border mt-10" />
            <img src="https://images.pexels.com/photos/3407777/pexels-photo-3407777.jpeg?w=600" alt="Calvados" className="w-full h-64 object-cover rounded-xl border border-border" />
            <img src="https://images.pexels.com/photos/34191830/pexels-photo-34191830.jpeg?w=600" alt="Cocktail signature" className="w-full h-64 object-cover rounded-xl border border-border mt-10" />
          </div>
        </div>
      </section>

      {/* DISTINCTIONS */}
      <section id="awards" className="relative overflow-hidden py-20 lg:py-28" data-testid="awards-section">
        <KintsugiScrollVein variant="diagonal" strokes={3} opacity={0.6} />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-primary/5 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-5 mb-6">
              <span className="h-px w-12 bg-primary/50" />
              <FleurKintsugi size={30} />
              <span className="h-px w-12 bg-primary/50" />
            </div>
            <div className="text-xs uppercase tracking-[0.35em] text-primary mb-4 font-semibold">Récompenses 2026</div>
            <h2 className="font-display text-4xl lg:text-5xl mb-5">Deux médailles au <em className="gold-text not-italic">Frankfurt International Trophy</em></h2>
            <p className="text-muted-foreground leading-relaxed">
              Nos rhums arrangés sont primés par l'un des concours internationaux les plus exigeants au monde — une reconnaissance du travail de nos artisans et de la richesse du terroir normand.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <AwardCard
              image="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/ddfc3ewe_Image%2011-04-2026%20a%CC%80%2008.29.jpeg"
              tier="Grand Or"
              tierClass="text-primary"
              product="Bois Sucré Intense"
              category="Rhum arrangé"
              year="2026"
              description="Une macération profonde d'épices boisées et de sucres nobles. Médaille Grand Or du Frankfurt International Trophy 2026."
              testid="award-grand-or"
            />
            <AwardCard
              image="https://customer-assets.emergentagent.com/job_cheers-book/artifacts/aihf2pmx_Image%2011-04-2026%20a%CC%80%2008.34.jpeg"
              tier="Argent"
              tierClass="text-zinc-300"
              product="Élixir des Anges"
              category="Rhum arrangé"
              year="2026"
              description="Infusion délicate, rondeur enveloppante et finale aromatique. Médaille d'Argent du Frankfurt International Trophy 2026."
              testid="award-argent"
            />
          </div>

          <div className="text-center mt-12">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              Retrouvez nos cuvées primées à la carte et lors de nos ateliers de rhum arrangé
            </p>
          </div>
        </div>
      </section>

      {/* MON HISTOIRE — aperçu */}
      <StoryTeaser
        eyebrow={c("story_teaser_eyebrow", "Mon histoire")}
        title={c("story_teaser_title", "Du garage au Grand Or de Francfort.")}
        text={c("story_teaser_text", "")}
        quote={c("story_lead_quote", "")}
        portrait={c("story_teaser_image", STOREFRONT_IMG_DEFAULT)}
        name={c("story_name", "Éric Awono")}
      />

      {/* MARQUEE / VALUES */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 overflow-hidden" data-testid="values-section">
        <KintsugiScrollVein variant="horizontal" strokes={2} opacity={0.4} />
        <div className="relative z-10 grid md:grid-cols-3 gap-10">
          <ValueCard icon={<Wine className="w-6 h-6" />} title="Mixologie d'auteur" text="Cartes saisonnières créées par notre chef barman, autour de spiritueux rares et d'ingrédients frais." />
          <ValueCard icon={<Sparkles className="w-6 h-6" />} title="Bar mobile clé en main" text="Logistique, équipement et brigade : nous nous déplaçons partout en France." />
          <ValueCard icon={<Users className="w-6 h-6" />} title="Ateliers premium" text="Petits groupes, gestes précis, dégustations guidées. Une vraie expérience." />
        </div>
      </section>

      {/* NOUS TROUVER */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 overflow-hidden" data-testid="find-us-section">
        <KintsugiScrollVein variant="s-curve" strokes={2} opacity={0.45} />
        <div className="relative z-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-5 mb-5">
            <span className="h-px w-10 bg-primary/50" />
            <FleurKintsugi size={26} />
            <span className="h-px w-10 bg-primary/50" />
          </div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4 font-semibold">· Nous trouver ·</div>
          <h2 className="font-display text-4xl sm:text-5xl">Poussez la porte du bar</h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Au cœur de Louviers, à 25 minutes de Rouen. Une cave de spiritueux d'exception, des cocktails d'auteur et une carte normande à découvrir.
          </p>
        </div>

        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 lg:gap-10">
          {/* Infos pratiques */}
          <div className="rounded-2xl border border-border bg-card p-8 lg:p-10 flex flex-col justify-between" data-testid="find-us-info">
            <div className="space-y-7">
              <ContactRow
                icon={<MapPin className="w-5 h-5" />}
                label="Adresse"
                lines={c("contact_address", "4 rue de la Laiterie, 27400 Louviers — Normandie").split(",").map(s => s.trim())}
              />
              <ContactRow
                icon={<Phone className="w-5 h-5" />}
                label="Téléphone"
                lines={[c("contact_phone_display", "06 65 02 87 00")]}
                href={`tel:+33${c("contact_phone_display", "0665028700").replace(/\D/g, "").replace(/^0/, "")}`}
              />
              <ContactRow
                icon={<Mail className="w-5 h-5" />}
                label="Email"
                lines={["e.awono@saveursnormandes.fr"]}
                href="mailto:e.awono@saveursnormandes.fr"
              />
              <ContactRow
                icon={<Clock className="w-5 h-5" />}
                label="Horaires"
                lines={c("contact_hours", "Mardi — Samedi · 17h — 23h\nDimanche & Lundi · privatisation sur demande").split("\n")}
              />
            </div>
            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=4+rue+de+la+Laiterie,+27400+Louviers"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 h-11 text-sm font-medium transition-colors"
                data-testid="find-us-itinerary-btn"
              >
                Itinéraire <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/reservation-table"
                className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-6 h-11 text-sm transition-colors"
                data-testid="find-us-book-btn"
              >
                Réserver une table
              </Link>
            </div>
          </div>

          {/* Carte Google Maps + devanture */}
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden border border-border bg-card relative aspect-[4/3] lg:aspect-auto lg:h-[300px]" data-testid="find-us-map">
              <iframe
                title="Carte Saveurs Normandes — 4 rue de la Laiterie, Louviers"
                src="https://www.google.com/maps?q=4+rue+de+la+Laiterie,+27400+Louviers,+France&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                className="absolute inset-0 w-full h-full grayscale-[35%] contrast-[0.95]"
              />
            </div>
            <div className="rounded-2xl overflow-hidden border border-border bg-card relative" data-testid="find-us-storefront">
              <img
                src={STOREFRONT_IMG}
                alt="Devanture du bar Saveurs Normandes au 4 rue de la Laiterie, Louviers"
                className="w-full h-[180px] lg:h-[110px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-4">
                <div className="text-xs uppercase tracking-[0.25em] text-white/90">La devanture · Louviers</div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 overflow-hidden" data-testid="home-cta">
        <KintsugiScrollVein variant="diagonal" strokes={2} opacity={0.45} />
        <div className="relative z-10 rounded-2xl border border-primary/30 bg-card p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h3 className="font-display text-3xl lg:text-4xl mb-3">Un projet sur mesure ?</h3>
            <p className="text-muted-foreground max-w-xl">Mariage, EVG, vin d'honneur, soirée d'entreprise — décrivez-nous votre événement et nous vous proposons une formule adaptée sous 48h.</p>
          </div>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 h-12" data-testid="cta-private-event-btn">
            <Link to="/evenement-prive">Demander un devis</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ to, image, tag, title, text, className = "", tall, testid }) {
  return (
    <Link
      to={to}
      className={`group relative overflow-hidden rounded-xl border border-primary/25 bg-card hover-lift ${className}`}
      data-testid={testid}
    >
      <div className={`relative ${tall ? "aspect-[4/5] md:aspect-auto md:h-full md:min-h-[520px]" : "aspect-[16/10]"}`}>
        <div className="absolute inset-0">
          <img src={image} alt={title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div className="absolute inset-0 p-7 flex flex-col justify-end">
          <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-2 font-semibold">{tag}</div>
          <h3 className="font-display text-3xl text-white mb-2" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}>{title}</h3>
          <p className="text-sm text-white/85 max-w-md">{text}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-primary text-sm font-medium">
            Découvrir <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
}

function ValueCard({ icon, title, text }) {
  return (
    <div className="p-6 rounded-xl border border-border bg-card hover-lift">
      <div className="w-11 h-11 rounded-full luxury-border flex items-center justify-center text-primary mb-5">
        {icon}
      </div>
      <h4 className="font-display text-xl mb-2">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function BarCard({ tag, title, text, image, testid, to, ctaLabel }) {
  const inner = (
    <>
      <div className="aspect-[4/3] overflow-hidden">
        <div className="w-full h-full">
          <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        </div>
      </div>
      <div className="p-6">
        <div className="text-[11px] uppercase tracking-[0.3em] text-primary mb-2 font-semibold">{tag}</div>
        <h3 className="font-display text-2xl mb-3">{title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
        {to && ctaLabel && (
          <div className="mt-4 inline-flex items-center gap-2 text-sm text-primary group-hover:gap-3 transition-all">
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </div>
        )}
      </div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="group rounded-xl overflow-hidden border border-border bg-card hover-lift block"
        data-testid={testid}
      >{inner}</Link>
    );
  }
  return (
    <article className="group rounded-xl overflow-hidden border border-border bg-card hover-lift" data-testid={testid}>
      {inner}
    </article>
  );
}

function ContactRow({ icon, label, lines, href }) {
  const body = (
    <div className="flex items-start gap-4">
      <div className="w-10 h-10 rounded-full border border-primary/40 flex items-center justify-center text-primary shrink-0 mt-0.5">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-1">{label}</div>
        {lines.map((l, i) => (
          <div key={`${label}-${i}`} className={i === 0 ? "text-base text-foreground" : "text-sm text-muted-foreground"}>{l}</div>
        ))}
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block hover:opacity-80 transition-opacity">{body}</a>
  ) : body;
}

function AwardCard({ image, tier, tierClass, product, category, year, description, testid }) {
  return (
    <article
      className="group relative rounded-2xl border border-primary/20 bg-card hover-lift overflow-hidden"
      data-testid={testid}
    >
      <div className="grid sm:grid-cols-[1fr_1.1fr] gap-0">
        <div className="relative bg-white p-4 sm:p-6 flex items-center justify-center">
          <img
            src={image}
            alt={`Distinction ${tier} — ${product}`}
            className="w-full h-auto max-h-72 object-contain transition-transform duration-500 group-hover:scale-[1.03]"
          />
        </div>
        <div className="p-7 lg:p-8 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-3">
            <span className={`font-display text-3xl lg:text-4xl ${tierClass}`}>{tier}</span>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground border border-border rounded-full px-3 py-1">
              {year}
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-3xl mb-1">{product}</h3>
          <div className="text-xs uppercase tracking-[0.25em] text-primary mb-4">{category}</div>
          <p className="text-sm text-muted-foreground leading-relaxed mb-5">{description}</p>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="inline-block h-1 w-1 rounded-full bg-primary" />
            Frankfurt International Trophy
          </div>
        </div>
      </div>
    </article>
  );
}
